import React, { useRef, useState } from 'react';
import './ReceiptModal.css';
import receiptService from '../services/receiptService';
import { runTrfPrint, describeTrfOutcome } from '../utils/trfPrint';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// dd-MMM-yyyy hh:mm AM — same format as the backend print-html page
const formatDateTime = (value) => {
  const d = value ? new Date(value) : new Date();
  if (Number.isNaN(d.getTime())) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  const h = d.getHours() % 12 || 12;
  return `${pad(d.getDate())}-${MONTHS[d.getMonth()]}-${d.getFullYear()} ${pad(h)}:${pad(d.getMinutes())} ${d.getHours() < 12 ? 'AM' : 'PM'}`;
};

const money = (v) => (Number(v) || 0).toFixed(2);

const getInitials = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || 'LB';

const getPaymentStatus = (paid, balance) => {
  if (balance <= 0.005) return { key: 'paid', label: 'Paid in Full' };
  if (paid > 0) return { key: 'partial', label: 'Partially Paid' };
  return { key: 'due', label: 'Payment Due' };
};

export const ReceiptModal = ({ isOpen, onClose, receipt }) => {
  const docRef = useRef(null);
  const [printing, setPrinting] = useState(false);
  const [trfBusy, setTrfBusy] = useState(false);
  const [trfMsg, setTrfMsg] = useState(null);

  if (!isOpen || !receipt) return null;

  const centreName = receipt.centreName || 'Auro Diagnostic Reference Laboratory';
  const net = Number(receipt.netAmount) || 0;
  const paid = Number(receipt.amountPaid) || 0;
  const balance = Number(receipt.balanceDue) || 0;
  const status = getPaymentStatus(paid, balance);
  const items = receipt.items || [];
  const payments = receipt.payments || [];

  // Print only the receipt document through an isolated iframe so the rest of
  // the app layout (fixed/overflow containers) can never blank out the page.
  const handlePrint = () => {
    const node = docRef.current;
    if (!node || printing) return;
    setPrinting(true);

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
    document.body.appendChild(iframe);

    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((el) => (el.tagName === 'LINK' ? `<link rel="stylesheet" href="${el.href}">` : el.outerHTML))
      .join('');

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Receipt ${receipt.receiptNo || receipt.ledgerTransactionNo || ''}</title>${styles}
      <style>@page { size: A4; margin: 10mm; } html, body { background: #fff !important; margin: 0; padding: 0; }</style>
      </head><body>${node.outerHTML}</body></html>`);
    doc.close();

    const cleanup = () => {
      setTimeout(() => iframe.remove(), 500);
      setPrinting(false);
    };

    const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'));
    const waitForStyles = Promise.all(
      links.map((l) => (l.sheet ? Promise.resolve() : new Promise((res) => { l.onload = res; l.onerror = res; })))
    );
    const timeout = new Promise((res) => setTimeout(res, 2000));

    Promise.race([waitForStyles, timeout]).then(() => {
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } finally {
          cleanup();
        }
      }, 100);
    });
  };

  // Separate from receipt printing: the server decides which Radiology tests are eligible and
  // how many TRFs to produce (combined / per-test) for this receipt's registration.
  const handlePrintTrf = async () => {
    if (trfBusy) return;
    setTrfBusy(true);
    setTrfMsg(null);
    try {
      const outcome = await runTrfPrint(receipt.ledgerTransactionNo, 'MANUAL');
      setTrfMsg(describeTrfOutcome(outcome, 'MANUAL'));
    } finally {
      setTrfBusy(false);
    }
  };

  const handleOpenPdfOrHtml = () => {
    const printUrl = receiptService.getPrintHtmlUrl(receipt.receiptNo || receipt.ledgerTransactionNo);
    window.open(printUrl, '_blank', 'width=900,height=950,menubar=no,toolbar=no,location=no');
  };

  return (
    <div className="receipt-modal-backdrop" onClick={onClose}>
      <div className="receipt-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Modal Toolbar */}
        <div className="receipt-modal-toolbar">
          <div className="receipt-modal-title">
            <span className="receipt-modal-title-main">📄 Official Receipt Preview</span>
            <span className="receipt-modal-title-sub">
              {receipt.receiptNo ? `Receipt ${receipt.receiptNo} · ` : ''}Lab No {receipt.ledgerTransactionNo}
            </span>
          </div>
          <div className="receipt-modal-actions">
            <button type="button" className="receipt-btn-print" onClick={handlePrint} disabled={printing}>
              🖨️ {printing ? 'Preparing…' : 'Print'}
            </button>
            <button type="button" className="receipt-btn-trf" onClick={handlePrintTrf} disabled={trfBusy}>
              🧾 {trfBusy ? 'Preparing TRF…' : 'Print TRF'}
            </button>
            <button type="button" className="receipt-btn-pdf" onClick={handleOpenPdfOrHtml}>
              📑 Open Document / PDF
            </button>
            <button type="button" className="receipt-btn-close" onClick={onClose}>
              ✕ Close
            </button>
          </div>
        </div>

        {/* Printable Body */}
        {trfMsg && (
          <div className={`receipt-trf-msg receipt-trf-msg--${trfMsg.type}`} role="status" aria-live="polite">
            {trfMsg.message}
          </div>
        )}
        <div className="receipt-modal-body">
          <div className="rcpt" ref={docRef}>
            <div className="rcpt-band" />
            <div className="rcpt-inner">
              {/* Header */}
              <header className="rcpt-head">
                <div className="rcpt-brand">
                  <div className="rcpt-logo">{getInitials(centreName)}</div>
                  <div>
                    <h1 className="rcpt-org">{centreName}</h1>
                    <p className="rcpt-org-meta">{receipt.centreAddress || 'Central Laboratory Facility'}</p>
                    <p className="rcpt-org-meta">Contact: {receipt.centreContact || '033-24567890'} · Email: info@aurolab.com</p>
                    <span className="rcpt-accred">NABL Accredited • ISO 15189 Certified Clinical Laboratory</span>
                  </div>
                </div>
                <div className="rcpt-doc">
                  <h2 className="rcpt-doc-title">Money Receipt</h2>
                  <p className="rcpt-doc-sub">Diagnostic Work Order</p>
                  <span className={`rcpt-status rcpt-status--${status.key}`}>{status.label}</span>
                </div>
              </header>

              {/* Key references */}
              <div className="rcpt-meta">
                <div>
                  <span className="rcpt-label">Receipt No</span>
                  <span className="rcpt-value rcpt-mono">{receipt.receiptNo || '—'}</span>
                </div>
                <div>
                  <span className="rcpt-label">Lab / Work Order No</span>
                  <span className="rcpt-value rcpt-mono">{receipt.ledgerTransactionNo || '—'}</span>
                </div>
                <div>
                  <span className="rcpt-label">Registration Date</span>
                  <span className="rcpt-value">{formatDateTime(receipt.billDate)}</span>
                </div>
                <div>
                  <span className="rcpt-label">Cashier / User</span>
                  <span className="rcpt-value">{receipt.createdBy || 'Front Desk'}</span>
                </div>
              </div>

              {/* Patient & billing */}
              <div className="rcpt-parties">
                <div className="rcpt-card">
                  <div className="rcpt-card-title">Patient Details</div>
                  <dl className="rcpt-dl">
                    <dt>Patient Name</dt>
                    <dd className="rcpt-strong">{receipt.patientName}</dd>
                    <dt>UHID</dt>
                    <dd className="rcpt-mono">{receipt.uhid || receipt.patientId}</dd>
                    <dt>Age / Gender</dt>
                    <dd>{receipt.ageGender}</dd>
                    <dt>Mobile No</dt>
                    <dd>{receipt.mobile}</dd>
                    <dt>Address</dt>
                    <dd>{receipt.address || 'N/A'}</dd>
                  </dl>
                </div>
                <div className="rcpt-card">
                  <div className="rcpt-card-title">Referral & Billing</div>
                  <dl className="rcpt-dl">
                    <dt>Ref. Doctor</dt>
                    <dd>{receipt.doctorName || 'Self / Direct'}</dd>
                    <dt>Rate Panel</dt>
                    <dd>{receipt.panelName || 'Direct Walkin'}</dd>
                    <dt>Payment Mode</dt>
                    <dd>{receipt.paymentMode || 'Cash'}</dd>
                    <dt>Tests Booked</dt>
                    <dd>{items.length}</dd>
                  </dl>
                </div>
              </div>

              {/* Diagnostic items */}
              <table className="rcpt-table rcpt-table--items">
                <thead>
                  <tr>
                    <th style={{ width: '34px' }}>#</th>
                    <th>Diagnostic Investigation</th>
                    <th>Department</th>
                    <th>Specimen Barcode</th>
                    <th className="num">Rate (₹)</th>
                    <th className="num">Disc. (₹)</th>
                    <th className="num">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? (
                    items.map((itm, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>
                          <span className="rcpt-item-name">{itm.itemName}</span>
                          {itm.itemCode && <span className="rcpt-item-code">Code: {itm.itemCode}</span>}
                        </td>
                        <td>{itm.department || '-'}</td>
                        <td>{itm.barcodeNo ? <span className="rcpt-chip">{itm.barcodeNo}</span> : '-'}</td>
                        <td className="num">{money(itm.rate)}</td>
                        <td className="num">{money(itm.discount)}</td>
                        <td className="num" style={{ fontWeight: 700 }}>{money(itm.amount)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="rcpt-empty">Diagnostic services rendered</td>
                    </tr>
                  )}
                </tbody>
                {items.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan="6" className="num">Total</td>
                      <td className="num">₹ {money(receipt.grossAmount)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>

              {/* Payments & totals */}
              <div className="rcpt-bottom">
                <div>
                  <div className="rcpt-pay-box">
                    <div className="rcpt-section-title">Payment Installments History</div>
                    <table className="rcpt-table rcpt-table--compact">
                      <thead>
                        <tr>
                          <th>Receipt No</th>
                          <th>Date</th>
                          <th>Mode</th>
                          <th>Type</th>
                          <th className="num">Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.length > 0 ? (
                          payments.map((p, idx) => (
                            <tr key={idx}>
                              <td className="rcpt-mono">{p.receiptNo}</td>
                              <td>{formatDateTime(p.paymentDate)}</td>
                              <td>{p.paymentMode}</td>
                              <td>
                                <span className={`rcpt-type ${p.isSettlement ? 'rcpt-type--settle' : 'rcpt-type--adv'}`}>
                                  {p.isSettlement ? 'Settlement' : 'Advance'}
                                </span>
                              </td>
                              <td className="num" style={{ fontWeight: 700 }}>{money(p.amount)}</td>
                            </tr>
                          ))
                        ) : paid > 0 ? (
                          <tr>
                            <td className="rcpt-mono">{receipt.receiptNo}</td>
                            <td>{formatDateTime(receipt.billDate)}</td>
                            <td>{receipt.paymentMode || 'Cash'}</td>
                            <td><span className="rcpt-type rcpt-type--adv">Advance</span></td>
                            <td className="num" style={{ fontWeight: 700 }}>{money(paid)}</td>
                          </tr>
                        ) : (
                          <tr>
                            <td colSpan="5" className="rcpt-empty">No payments recorded yet</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="rcpt-words">
                    <strong>Received with thanks: </strong>
                    <span>{receipt.amountInWords || 'Amount as acknowledged above.'}</span>
                  </div>
                </div>

                <div className="rcpt-summary">
                  <div className="rcpt-sum-row">
                    <span>Gross Total Amount</span>
                    <span>₹ {money(receipt.grossAmount)}</span>
                  </div>
                  {(Number(receipt.discountAmount) || 0) > 0 && (
                    <div className="rcpt-sum-row rcpt-sum-row--disc">
                      <span>Discount / Concession</span>
                      <span>- ₹ {money(receipt.discountAmount)}</span>
                    </div>
                  )}
                  {(Number(receipt.collectionCharge) || 0) > 0 && (
                    <div className="rcpt-sum-row">
                      <span>Collection Charge</span>
                      <span>+ ₹ {money(receipt.collectionCharge)}</span>
                    </div>
                  )}
                  <div className="rcpt-sum-row rcpt-sum-row--net">
                    <span>Net Bill Amount</span>
                    <span>₹ {money(net)}</span>
                  </div>
                  <div className="rcpt-sum-row rcpt-sum-row--paid">
                    <span>Total Amount Paid</span>
                    <span>₹ {money(paid)}</span>
                  </div>
                  <div className={`rcpt-balance ${balance > 0.005 ? 'rcpt-balance--due' : 'rcpt-balance--clear'}`}>
                    <span className="rcpt-label">Balance Due</span>
                    <strong>₹ {money(balance)}</strong>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <footer className="rcpt-foot">
                <ul className="rcpt-terms">
                  <li>Reports will only be handed over on presentation of this original receipt.</li>
                  <li>Please verify all demographic details before leaving the front desk.</li>
                  <li>Online patient portal reports accessible for 7 days via UHID.</li>
                </ul>
                <div className="rcpt-sign">
                  <div className="rcpt-sign-line">Authorised Cashier / Front Office</div>
                </div>
              </footer>
              <div className="rcpt-note">
                This is a computer-generated receipt · Printed on {formatDateTime(new Date())}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
